<template>
  <div v-if="Object.keys(filterOptions).length" class="cf__item" :class="{ active: isActive }">
    <div v-if="isListingPage" class="cf__title">FILTER BY SIZE</div>
    <div
      v-if="!isListingPage"
      class="cf__title-wrapper"
      @click="toggleFilterModule"
      @keydown="handleKeyDownOnFilterAccordion"
      tabindex="0"
      role="button"
      :aria-expanded="isActive"
      :aria-controls="'filter-panel-' + filterType"
      :aria-label="ariaLabel"
    >
      <div class="cf__title">
        {{ filterType === 'percentageOff' ? 'percentage Off' : filterType }}
        <span v-if="checkedOptions.length">({{ checkedOptions.length }})</span>
      </div>
      <svg :class="['cf__icon']">
        <use xlink:href="#icon-prev"></use>
      </svg>
    </div>
    <div class="cf__content-outer-wrapper" :id="'filter-panel-' + filterType" :aria-hidden="!isActive">
      <div class="cf__content-wrapper" style="--offset-height: 16.8rem" data-oheight="168">
        <div v-for="(count, name) in displayFilterOptions" :key="name" class="cf__content">
          <label
            v-if="filterType === 'color'"
            :class="[
              'cf__checkbox',
              { checked: checkedOptions.some((option) => isEqual(option, colorHexCode(name, 'name'))) },
            ]"
            :tabindex="isActive ? 0 : -1"
            :for="name"
            @keydown="(e) => handleKeyDownOnColorFilterClick(e, filterType, colorHexCode(name, 'name'), name)"
          >
            <span class="cf__checkbox-color">
              <input
                type="checkbox"
                :value="name"
                :id="name"
                :aria-label="`Filter products by color ${colorHexCode(name, 'name')}`"
                :checked="checkedOptions.some((option) => isEqual(option, colorHexCode(name, 'name')))"
                @change="(e) => updateCheckedInput(e, filterType, colorHexCode(name, 'name'))"
                tabindex="-1"
                aria-hidden="true"
                :aria-checked="checkedOptions.some((option) => isEqual(option, colorHexCode(name, 'name')))"
              />
              <span
                :class="['cf__checkbox-control', { white: colorHexCode(name, 'hexcode') === '#fffff4' }]"
                :style="{ '--hex-code': colorHexCode(name, 'hexcode') }"
                role="presentation"
              >
              </span>
              <div aria-live="polite" class="cf__visually-hidden">
                {{
                  checkedOptions.some((option) => isEqual(option, colorHexCode(name, 'name')))
                    ? `selected: Filter products by color ${colorHexCode(name, 'name')}`
                    : `deselected: Filter products by color ${colorHexCode(name, 'name')}`
                }}
              </div>
            </span>
            <span class="cf__label" aria-hidden="true">{{ colorHexCode(name, 'name') }} {{ `(${count})` }}</span>
          </label>
          <label
            v-else
            :class="['cf__checkbox', { checked: checkedOptions.some((option) => isEqual(option, name)) }]"
            :for="`filter-${name}`"
          >
            <input
              type="checkbox"
              :value="name"
              :id="`filter-${name}`"
              :aria-label="`Filter products by ${filterType} ${name}`"
              :checked="checkedOptions.some((option) => isEqual(option, name))"
              :aria-checked="checkedOptions.some((option) => isEqual(option, name))"
              @change="(e) => updateCheckedInput(e, filterType, name)"
            />
            <span class="cf__label">{{ name }} {{ `(${count})` }}</span>
          </label>
        </div>
        <button
          v-if="Object.keys(filterOptions).length > 6 && !isListingPage"
          @click="toggleShowMore()"
          tabindex="0"
          class="cf__show-more"
        >
          {{ initialCount > 6 ? 'Show Less' : 'Show More' }}
        </button>

        <!-- Price Filter -->
        <!-- <form v-if="filterType === 'price'" class="cf__priceRangeForm">
          <div class="cf__priceRange-inputWrapper">
            <span class="cf__dollarSign">$</span>
            <input 
              type="number"
              class="cf__priceRange-input"
              pattern="type === 'number' ? '[0-9]+' : '.*'"
            >
          </div>
          <span>-</span>
          <div class="cf__priceRange-inputWrapper">
            <span class="cf__dollarSign">$</span>
            <input 
              type="number"
              class="cf__priceRange-input"
              pattern="type === 'number' ? '[0-9]+' : '.*'"
            >
          </div>
          <button type="submit" class="cf__priceRangeSubmit">Go</button>
        </form> -->
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, toRefs, computed, onMounted, watch } from 'vue';
  import isEqual from 'lodash/isEqual';

  const props = defineProps({
    filterType: {
      type: String,
      required: true,
    },

    filterOptions: {
      type: Array,
      required: true,
    },

    appliedFilters: {
      type: Object,
      required: true,
    },

    isListingPage: {
      type: Boolean,
      default: false,
    },
  });

  const { filterType, filterOptions, appliedFilters, isListingPage } = toRefs(props);
  const initialCount = ref(isListingPage.value ? 20 : 6);

  const emit = defineEmits(['input-change']);
  const checkedOptions = ref([]);
  const isActive = ref(true); // Reactive state to track the active state of the accordion.

  onMounted(() => {
    if (appliedFilters.value) {
      checkedOptions.value = appliedFilters.value;
    }
  });

  // Computed property for aria-label
  const ariaLabel = computed(() => {
    return isActive.value
      ? `Collapse filter options for ${filterType.value}`
      : `Expand filter options for ${filterType.value}`;
  });

  const displayFilterOptions = computed(() => {
    return Object.fromEntries(Object.entries(filterOptions.value).slice(0, initialCount.value));
  });

  const toggleShowMore = () => {
    if (initialCount.value === 6) {
      initialCount.value = Object.keys(filterOptions.value).length;
    } else {
      initialCount.value = 6;
    }
  };

  const colorHexCode = (name, type) => {
    return name.split('/')[type === 'hexcode' ? 1 : 0];
  };

  const toggleFilterModule = () => {
    isActive.value = !isActive.value;
  };

  const handleKeyDownOnFilterAccordion = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      toggleFilterModule();
      event.preventDefault();
    }
  };

  const updateCheckedInput = (event, type, option) => {
    if (event.target.checked) {
      checkedOptions.value = [...checkedOptions.value, option];
    } else {
      checkedOptions.value = checkedOptions.value.filter((checkedOption) => !isEqual(checkedOption, option));
    }

    emit('input-change', type, checkedOptions.value);
  };

  const handleKeyDownOnColorFilterClick = (e, filterType, option, name) => {
    if (e.key === 'Enter' || e.key === ' ') {
      const checkbox = document.getElementById(name);
      const simulatedEvent = { target: checkbox, type: 'change' };

      checkbox.checked = !checkbox.checked;

      updateCheckedInput(simulatedEvent, filterType, option);
      e.preventDefault();
    }
  };

  watch(appliedFilters, (value) => {
    const newCheckedOptions = value || [];

    if (!isEqual(checkedOptions.value, newCheckedOptions)) {
      checkedOptions.value = newCheckedOptions;
    }
  });
</script>
